import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Link,
  Select,
  SimpleGrid,
  Stack,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
} from '@chakra-ui/react';
import axiosInstance from '../../../api/AxiosInstance';

interface Props {
  modo: 'new' | 'edit' | 'view';
  data?: any;
  onGuardado?: () => void;
  onCancelar?: () => void;
}

const getBaseOrigin = () => {
  const configured = axiosInstance.defaults.baseURL as string | undefined;
  if (!configured) return window.location.origin;
  try {
    return new URL(configured, window.location.origin).origin;
  } catch {
    return window.location.origin;
  }
};

const resolveAssetUrl = (value: string) => {
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) return value;
  return `${getBaseOrigin()}${value.startsWith('/') ? '' : '/'}${value}`;
};

const FormularioPlanTurismo: React.FC<Props> = ({ modo, data, onGuardado, onCancelar }) => {
  const [form, setForm] = useState<any>({
    anio: '',
    nombre: '',
    objetivo: '',
    descripcion: '',
    fechaInicio: '',
    fechaFin: '',
    responsable: '',
    presupuesto: '',
    estatus: 'borrador',
  });
  const [docActual, setDocActual] = useState('');
  const [docNuevo, setDocNuevo] = useState<File | null>(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        anio: data.anio || '',
        nombre: data.nombre || '',
        objetivo: data.objetivo || '',
        descripcion: data.descripcion || '',
        fechaInicio: data.fechaInicio ? data.fechaInicio.split('T')[0] : '',
        fechaFin: data.fechaFin ? data.fechaFin.split('T')[0] : '',
        responsable: data.responsable || '',
        presupuesto: data.presupuesto || '',
        estatus: data.estatus || 'borrador',
      });
      setDocActual(data.docUrl || '');
      setDocNuevo(null);
    }
  }, [data]);

  const handleChange = (field: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.anio || !form.nombre || !form.fechaInicio || !form.fechaFin || !form.responsable) {
      return;
    }
    setGuardando(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, String(v ?? '')));
      fd.append('docActual', docActual || '');
      if (docNuevo) fd.append('documento', docNuevo);

      if (modo === 'edit' && data?.id) {
        await axiosInstance.put(`/turismo/planes/${data.id}`, fd);
      } else {
        await axiosInstance.post('/turismo/planes', fd);
      }
      onGuardado && onGuardado();
    } finally {
      setGuardando(false);
    }
  };

  const disabled = modo === 'view';

  return (
    <Box>
      <Stack spacing={5}>
        <Box borderWidth="1px" borderRadius="lg" p={4} bg="#f7efe8">
          <Text fontWeight="bold" color="#5b182b" mb={3}>Informacion general del plan</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl isRequired>
              <FormLabel>Anio</FormLabel>
              <Input type="number" value={form.anio} onChange={(e) => handleChange('anio', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Nombre</FormLabel>
              <Input value={form.nombre} onChange={(e) => handleChange('nombre', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Responsable</FormLabel>
              <Input value={form.responsable} onChange={(e) => handleChange('responsable', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Presupuesto</FormLabel>
              <Input type="number" value={form.presupuesto} onChange={(e) => handleChange('presupuesto', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl>
              <FormLabel>Estatus</FormLabel>
              <Select value={form.estatus} onChange={(e) => handleChange('estatus', e.target.value)} isDisabled={disabled}>
                <option value="borrador">Borrador</option>
                <option value="activo">Activo</option>
                <option value="concluido">Concluido</option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Documento del plan (archivo)</FormLabel>
              {!disabled ? (
                <>
                  <Input type="file" accept=".pdf,image/*" onChange={(e) => setDocNuevo(e.target.files?.[0] || null)} p={1} />
                  <FormHelperText>Sube PDF o imagen en lugar de URL.</FormHelperText>
                </>
              ) : (
                <Text fontSize="sm" color="gray.500">Solo lectura</Text>
              )}
              {docActual && (
                <Box mt={2}>
                  <Tag colorScheme="blue" borderRadius="full">
                    <TagLabel><Link href={resolveAssetUrl(docActual)} isExternal>{docActual.split('/').pop()}</Link></TagLabel>
                    {!disabled && <TagCloseButton onClick={() => setDocActual('')} />}
                  </Tag>
                </Box>
              )}
              {docNuevo && (
                <Box mt={2}>
                  <Tag colorScheme="green" borderRadius="full">
                    <TagLabel>{docNuevo.name}</TagLabel>
                    {!disabled && <TagCloseButton onClick={() => setDocNuevo(null)} />}
                  </Tag>
                </Box>
              )}
            </FormControl>
          </SimpleGrid>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4}>
          <Text fontWeight="bold" color="#5b182b" mb={3}>Vigencia del plan (rango de fechas)</Text>
          <HStack spacing={4} align="end" flexWrap="wrap">
            <FormControl isRequired minW={{ base: '100%', md: '240px' }}>
              <FormLabel>Fecha inicio</FormLabel>
              <Input type="date" value={form.fechaInicio} onChange={(e) => handleChange('fechaInicio', e.target.value)} isDisabled={disabled} />
            </FormControl>
            <FormControl isRequired minW={{ base: '100%', md: '240px' }}>
              <FormLabel>Fecha fin</FormLabel>
              <Input type="date" min={form.fechaInicio || undefined} value={form.fechaFin} onChange={(e) => handleChange('fechaFin', e.target.value)} isDisabled={disabled} />
            </FormControl>
          </HStack>
        </Box>

        <Box borderWidth="1px" borderRadius="lg" p={4} bg="gray.50">
          <Text fontWeight="bold" color="#5b182b" mb={3}>Objetivos y descripcion</Text>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Objetivo</FormLabel>
              <Textarea value={form.objetivo} onChange={(e) => handleChange('objetivo', e.target.value)} isDisabled={disabled} rows={4} />
            </FormControl>
            <FormControl>
              <FormLabel>Descripcion</FormLabel>
              <Textarea value={form.descripcion} onChange={(e) => handleChange('descripcion', e.target.value)} isDisabled={disabled} rows={4} />
            </FormControl>
          </SimpleGrid>
        </Box>
      </Stack>

      {modo !== 'view' && (
        <Box mt={6} display="flex" gap={3} justifyContent="flex-end">
          <Button onClick={onCancelar} variant="outline">Cancelar</Button>
          <Button bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={handleSubmit} isLoading={guardando} loadingText="Guardando">
            Guardar
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default FormularioPlanTurismo;
